import React, { useState } from 'react';
import { styled, useTheme } from '@mui/material';
import { GoalPriorityChip } from '../GoalPriorityChip/GoalPriorityChip';
import { Typography } from '../../atoms/Typography/Typography';
import { UniversalCard } from '../UniversalCard/UniversalCard';
import { useTranslation } from 'react-i18next';
import { ConfirmationCentered } from '../ConfirmationDialog/ConfirmationDialog';
import { useAppSelector, useAppDispatch } from '../../../common/hooks';
import { saveGoal } from '../../../features/client-goals';
import { checkFFEnabled, formatCurrency } from '../../../utils';
import { FEATURE_NAMES, invalidDateText } from '../../../common/constants';

export interface GoalSummaryChipProps {
    headerIcon: React.ReactNode | React.ReactNode[];
    goalType: string;
    isRetirement?: boolean;
    goal_name: string;
    priority: 'Need' | 'Want' | 'Wish' | 'Dream';
    goalAmount: number;
    target_date_data: string;
    retirement_age?: number;
    goalId: string;
    targetedRetirementIncome?: number;
    planningDate: string | null;
    // eslint-disable-next-line no-unused-vars
    onGoalDelete: (goalId: string) => void;
    onEdit?: () => void;
    showErrorBorder?: boolean;
}

const StyledDivLeft = styled('div')({
    display: 'flex',
    justifyContent: 'left',
    flexDirection: 'column',
    marginRight: '0.75rem'
});

const StyledDivRight = styled('div')({
    display: 'flex',
    justifyContent: 'left',
    alignItems: 'center',
    width: '30%'
});

const StyledContentContainer = styled('div')({
    display: 'flex',
    flexDirection: 'column',
    marginTop: '20px',
    gap: '20px'
});

const StyledDivPriority = styled('div')({
    display: 'flex',
    alignItems: 'left'
});

export const styledMainDiv = styled('div')({
    paddingTop: '0px'
});

export const StyledTable = styled('table')({
    width: '100%',
    borderCollapse: 'collapse',
    '& tr': {
        verticalAlign: 'top',
        '& td': {
            padding: 0
        }
    }
});

export const GoalSummaryChip = ({
    goal_name,
    goalType,
    headerIcon,
    priority,
    goalAmount,
    target_date_data,
    planningDate,
    isRetirement,
    goalId,
    onGoalDelete,
    onEdit,
    targetedRetirementIncome,
    showErrorBorder
}: GoalSummaryChipProps) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const dispatch = useAppDispatch();
    const adviceGoals = useAppSelector((state) => state.adviceGoals);
    const onConfirmDelete = () => {
        const remainingFormFields = adviceGoals.formFields.filter((goal) => goal.goalId !== goalId);
        const formFieldsForDelete = checkFFEnabled(FEATURE_NAMES['BACKEND_MANAGED_ID_TIMESTAMPS_FE_AEX-3021'])
            ? remainingFormFields.map((goal: any) =>
                  goal.goalId ? { ...goal, data: { ...goal.data, update: false } } : goal
              )
            : remainingFormFields;
        let updatedGoalData = {
            ...adviceGoals,
            formFields: formFieldsForDelete,
            response: [],
            global: {
                ...adviceGoals.global,
                enablePlanDetails: false
            }
        };
        dispatch(saveGoal(updatedGoalData));
        setShowDeleteModal(false);
        onGoalDelete(goalId);
    };

    return (
        <UniversalCard
            header={t(goalType?.toUpperCase())}
            headerIcon={headerIcon}
            actionMenuOptions={[
                {
                    label: t('LABEL_EDIT'),
                    onClick: () => {
                        if (onEdit) onEdit();
                    },
                    icon: 'edit'
                },
                {
                    label: t('DELETE_TEXT'),
                    onClick: () => setShowDeleteModal(!showDeleteModal),
                    icon: 'delete'
                }
            ]}
            onClickViewAll={() => {}}
            showSubcontent={false}
            cardSx={{
                padding: '20px 24px',
                '& .MuiCardHeader-root': { paddingTop: 0 },
                '& .MuiCardHeader-action': { margin: 0, alignSelf: 'center' }
            }}
            showErrorBorder={showErrorBorder}
        >
            <StyledContentContainer>
                <div>
                    <Typography
                        variant="labels.large"
                        label={t('GOAL_SUMMARY_DETAIL_TEXT')}
                        sx={{ color: theme.palette.neutral[300] }}
                        mb="4px"
                    />
                    <StyledDivPriority>
                        <StyledDivLeft>
                            <Typography variant="headers.h4" color="neutral.black" label={goal_name} />
                        </StyledDivLeft>
                        <StyledDivRight>
                            {priority && <GoalPriorityChip goalPriority={priority} />} &nbsp;
                        </StyledDivRight>
                    </StyledDivPriority>
                </div>
                <StyledTable>
                    <tr>
                        {isRetirement && targetedRetirementIncome && (
                            <td>
                                <Typography
                                    variant="labels.large"
                                    label={t('GOAL_SUMMARY_TARGET_INCOME_TEXT')}
                                    sx={{ color: theme.palette.neutral[300] }}
                                    mb="4px"
                                />
                            </td>
                        )}
                        {goalAmount ? (
                            <td>
                                <Typography
                                    variant="labels.large"
                                    label={
                                        isRetirement
                                            ? t('GOAL_SUMMARY_BEQUEST_AMOUNT_TEXT')
                                            : t('GOAL_SUMMARY_TARGET_AMOUNT_TEXT')
                                    }
                                    sx={{ color: theme.palette.neutral[300] }}
                                    mb="4px"
                                />
                            </td>
                        ) : null}
                    </tr>
                    <tr>
                        {isRetirement && targetedRetirementIncome && (
                            <td>
                                <Typography
                                    variant="headers.h4"
                                    color="neutral.black"
                                    label={formatCurrency(targetedRetirementIncome)}
                                />
                            </td>
                        )}
                        {goalAmount ? (
                            <td>
                                <Typography
                                    variant="headers.h4"
                                    color="neutral.black"
                                    label={formatCurrency(goalAmount)}
                                />
                            </td>
                        ) : null}
                    </tr>
                    <tr style={{ height: '20px' }}></tr>
                    <tr>
                        <td>
                            <Typography
                                variant="labels.large"
                                label={
                                    isRetirement
                                        ? t('GOAL_SUMMARY_RETIREMENT_DATE_TEXT')
                                        : t('GOAL_SUMMARY_TARGET_DATE_TEXT')
                                }
                                sx={{ color: theme.palette.neutral[300] }}
                                mb="4px"
                            />
                        </td>
                        {isRetirement && planningDate && (
                            <td>
                                <Typography
                                    variant="labels.large"
                                    label={t('PLANNING_DATE')}
                                    sx={{ color: theme.palette.neutral[300] }}
                                    mb="4px"
                                />
                            </td>
                        )}
                    </tr>
                    <tr>
                        <td>
                            <Typography
                                variant="headers.h4"
                                color="neutral.black"
                                label={
                                    target_date_data && target_date_data !== invalidDateText ? target_date_data : '-'
                                }
                            />
                        </td>
                        {isRetirement && planningDate && (
                            <td>
                                <Typography
                                    variant="headers.h4"
                                    color="neutral.black"
                                    label={planningDate && planningDate !== invalidDateText ? planningDate : '-'}
                                />
                            </td>
                        )}
                    </tr>
                </StyledTable>
            </StyledContentContainer>
            {showDeleteModal && (
                <ConfirmationCentered
                    cancelButtonLabel={`${t('BUTTON_CANCEL')}`}
                    confirmButtonLabel={`${t('DELETE_CONFIRMATION_MODAL_DELETE_TEXT')}`}
                    title={`${t('DELETE_GOAL_TITLE')}`}
                    content={`${t('DELETE_GOAL_CONTENT')}`}
                    onCancellation={() => setShowDeleteModal(false)}
                    onCloseIconClick={() => setShowDeleteModal(false)}
                    onConfirmation={() => onConfirmDelete()}
                    open
                />
            )}
        </UniversalCard>
    );
};
